"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Pathfinder = exports.getNextPiecePosition = void 0;
const board_1 = require("@creature-chess/board");
const getTargetAttackPositions_1 = require("./targeting/utils/getTargetAttackPositions");
const findPathFirstStep = (board, size, start, goals, facingNorth) => {
    if (goals.size === 0) {
        return null;
    }
    const forwardY = facingNorth ? -1 : 1;
    /**
     * Forward, right, backward, left relative to the attacker's facing.
     */
    const dx = [0, forwardY, 0, -forwardY];
    const dy = [forwardY, 0, -forwardY, 0];
    const width = size.width;
    const height = size.height;
    const visited = new Uint8Array(width * height);
    const startX = (0, board_1.unpackX)(start);
    const startY = (0, board_1.unpackY)(start);
    visited[startY * width + startX] = 1;
    /**
     * Parallel arrays for the BFS queue - cheaper than an array of objects.
     */
    const queuePos = [];
    const queueFirst = [];
    const queueDist = [];
    // Seed queue with start's neighbors. Each becomes its own firstStep.
    for (let i = 0; i < 4; i += 1) {
        const nx = startX + dx[i];
        const ny = startY + dy[i];
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) {
            continue;
        }
        const cellIdx = ny * width + nx;
        if (visited[cellIdx]) {
            continue;
        }
        if (board.getPieceIdAtPosition(nx, ny) !== null) {
            continue;
        }
        visited[cellIdx] = 1;
        const packed = (0, board_1.packPosition)(nx, ny);
        if (goals.has(packed)) {
            return { stepCount: 1, firstStep: packed };
        }
        queuePos.push(packed);
        queueFirst.push(packed);
        queueDist.push(1);
    }
    let head = 0;
    while (head < queuePos.length) {
        const curPacked = queuePos[head];
        const curFirst = queueFirst[head];
        const curDist = queueDist[head];
        head += 1;
        const cx = (0, board_1.unpackX)(curPacked);
        const cy = (0, board_1.unpackY)(curPacked);
        for (let i = 0; i < 4; i += 1) {
            const nx = cx + dx[i];
            const ny = cy + dy[i];
            if (nx < 0 || nx >= width || ny < 0 || ny >= height) {
                continue;
            }
            const cellIdx = ny * width + nx;
            if (visited[cellIdx]) {
                continue;
            }
            if (board.getPieceIdAtPosition(nx, ny) !== null) {
                continue;
            }
            visited[cellIdx] = 1;
            const packed = (0, board_1.packPosition)(nx, ny);
            if (goals.has(packed)) {
                return {
                    stepCount: curDist + 1,
                    firstStep: curFirst,
                };
            }
            queuePos.push(packed);
            queueFirst.push(curFirst);
            queueDist.push(curDist + 1);
        }
    }
    return null;
};
const getNextPiecePosition = (pathfinder, attackerPosition, 
/**
 * Is the attacker looking up the board? (i.e. facing negative y)
 */
attackerFacingUp, attackRange, targetPosition, board) => {
    const size = pathfinder.getSize();
    const goalList = (0, getTargetAttackPositions_1.getTargetAttackPositions)(size, targetPosition, attackRange);
    const goals = new Set(goalList);
    const result = findPathFirstStep(board, size, attackerPosition, goals, attackerFacingUp);
    if (!result) {
        return null;
    }
    if ((0, board_1.unpackX)(result.firstStep) < 0 || (0, board_1.unpackY)(result.firstStep) < 0) {
        throw new Error("Invalid path");
    }
    return result.firstStep;
};
exports.getNextPiecePosition = getNextPiecePosition;
/**
 * @note preserved for API compatibility.
 */
class Pathfinder {
    constructor(size) {
        this.size = size;
    }
    getSize() {
        return this.size;
    }
    getFirstStep(board, start, end) {
        const goals = new Set([end]);
        return findPathFirstStep(board, this.size, start, goals, true);
    }
}
exports.Pathfinder = Pathfinder;
